import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useNostr } from '@nostrify/react';
import { useCurrentUser } from './useCurrentUser';

interface LnurlPayResponse {
  callback: string;
  minSendable: number;
  maxSendable: number;
  allowsNostr?: boolean;
  nostrPubkey?: string;
}

/** Resolves a NIP-57 zap for an event: lud16 → lnurl → 9734 → invoice. */
export function useZapFlow(event: NostrEvent | undefined) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['stashstr', 'zapFlow', event?.id ?? '', user?.pubkey ?? ''],
    queryFn: async (c) => {
      if (!event) throw new Error('No event');
      if (!user) throw new Error('Not logged in');

      // 1. Get recipient's lud16/lud06 from kind 0
      const [meta] = await nostr.query(
        [{ kinds: [0], authors: [event.pubkey], limit: 1 }],
        { signal: c.signal },
      );
      let lud16: string | undefined;
      try {
        const metadata = JSON.parse(meta?.content ?? '{}') as { lud16?: string; lud06?: string };
        lud16 = metadata.lud16 ?? metadata.lud06;
      } catch {
        lud16 = undefined;
      }
      if (!lud16) throw new Error('Recipient has no lightning address');

      // 2. Resolve lnurl-pay endpoint
      let lnurlPayUrl: string;
      if (lud16.includes('@')) {
        const [name, domain] = lud16.split('@');
        lnurlPayUrl = `https://${domain}/.well-known/lnurlp/${name}`;
      } else if (lud16.startsWith('lnurl')) {
        lnurlPayUrl = lud16;
      } else {
        throw new Error('Invalid lightning address');
      }

      const res = await fetch(lnurlPayUrl, { signal: c.signal });
      if (!res.ok) throw new Error('Failed to fetch lnurl-pay');
      const pay = (await res.json()) as LnurlPayResponse;
      if (!pay.allowsNostr || !pay.nostrPubkey) {
        throw new Error('Recipient wallet does not support Nostr zaps');
      }

      return { lud16, pay };
    },
    staleTime: 5 * 60_000,
    enabled: Boolean(event) && Boolean(user),
    retry: false,
  });
}

/** Requests an invoice from the recipient's wallet for a zap request event. */
export async function fetchZapInvoice(
  callback: string,
  zapRequestEvent: NostrEvent,
  amountMsats: number,
  lnurl: string,
): Promise<string> {
  const params = new URLSearchParams({
    amount: String(amountMsats),
    nostr: JSON.stringify(zapRequestEvent),
    lnurl,
  });
  const separator = callback.includes('?') ? '&' : '?';
  const res = await fetch(`${callback}${separator}${params.toString()}`);
  if (!res.ok) throw new Error('Zap callback failed');
  const json = (await res.json()) as { pr?: string; status?: string; reason?: string };
  if (json.status === 'ERROR') throw new Error(json.reason ?? 'Zap rejected');
  if (!json.pr) throw new Error('No invoice returned');
  return json.pr;
}

/** Hook to manage WebLN payment if available. */
export function useWebLn() {
  const [hasWebln, setHasWebln] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'webln' in window) {
      setHasWebln(true);
    }
  }, []);

  const pay = async (invoice: string): Promise<void> => {
    if (!('webln' in window)) throw new Error('No WebLN provider');
    const webln = (window as { webln?: { sendPayment: (inv: string) => Promise<unknown> } }).webln;
    if (!webln) throw new Error('No WebLN provider');
    await webln.sendPayment(invoice);
  };

  return { hasWebln, pay };
}
